
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Product, SizeGuide } from "@/types/product";
import { products as localProducts } from "@/data/products";

export const useProducts = () => {
  return useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      try {
        // Essai via le backend
        const { data, error } = await (supabase as any)
          .from('products')
          .select(`
            id,
            name,
            description,
            price,
            published,
            has_sizes,
            has_colors,
            has_size_guide,
            size_guide,
            slug,
            product_categories!product_categories_product_id_fkey (
              categories (
                name
              )
            ),
            product_variants!product_variants_product_id_fkey (
              id,
              size,
              color_name,
              color_hex,
              position,
              price
            ),
            product_images!product_images_product_id_fkey (
              url,
              main
            )
          `)
          .eq('published', true)
          .order('created_at', { ascending: false });

        if (error) throw error;

        if (!data || data.length === 0) {
          return localProducts.map(p => ({ ...p, published: true }));
        }

        const mappedProducts = data.map((product: any): Product => {
          const categories = product.product_categories
            ? product.product_categories.map((pc: any) => pc.categories.name)
            : [];
          const sortedVariants = Array.isArray(product.product_variants)
            ? [...product.product_variants].sort((a: any, b: any) => (a.position || 0) - (b.position || 0))
            : [];
          const images = Array.isArray(product.product_images)
            ? [...product.product_images]
                .sort((a: any, b: any) => (b.main ? 1 : 0) - (a.main ? 1 : 0))
                .map((image: any) => {
                  let imageUrl = image.url;
                  if (imageUrl && imageUrl.startsWith('public/')) {
                    imageUrl = imageUrl.replace('public/', '/');
                  }
                  return imageUrl;
                })
            : [];

          return {
            id: product.id,
            name: product.name,
            description: product.description,
            price: product.price,
            categories,
            published: product.published,
            has_sizes: product.has_sizes,
            has_colors: product.has_colors,
            has_size_guide: product.has_size_guide,
            size_guide: product.size_guide as SizeGuide | null,
            slug: product.slug,
            variants: sortedVariants.map((variant: any) => ({
              size: variant.size,
              colorName: variant.color_name,
              colorHex: variant.color_hex,
              position: variant.position,
              price: variant.price
            })),
            images
          };
        });

        return mappedProducts;
      } catch (e) {
        return localProducts.map(p => ({ ...p, published: true }));
      }
    },
    staleTime: 0, // Ne pas mettre en cache pour forcer le rafraîchissement
    refetchOnWindowFocus: true, // Recharger quand la fenêtre reprend le focus
    refetchOnMount: true, // Recharger à chaque montage
    retry: 2 // Réessayer en cas d'échec
  });
};
